import axios from "axios";
import pako from "pako";
import API from "./api";
import {
    GetCategoriesResp,
    GetDivisionAttributesResp,
    GetDivisionsFullResp,
    GetDivisionsResp,
    GetUsersResp,
    HistoryResp,
    LoginResp,
    SuccessResponse,
    UpdateCategoryResp,
    UpdateDivisionAttributeResp,
    UpdateDivisionResp,
    UpdateUserResp, UploadCSVResp,
    VerifyTokenResp
} from "./response-types";
import {AttTestOperator, GroupOperation, IndicatorSiblingType, Role} from "./enums";

const simpleOperationsTimeout = 10_000;
const mediumOperationsTimeout = 25_000;
const complexOperationsTimeout = 600_000;

axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'True';


// region AuthOperations
export async function testAuth(): Promise<VerifyTokenResp> {
    return axios
        .post(API.VERIFY_TOKEN, {}, {timeout: simpleOperationsTimeout})
        .then(({data}) => {
            return data as VerifyTokenResp;
        }).catch((reason) => {
            return {success: false, role: Role.User, user_id: 0, errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as VerifyTokenResp;
        });
}

export async function checkConnection(): Promise<SuccessResponse> {
    // don't need connection validation when serving frontend from FastAPI
    return {success: true} as SuccessResponse;
}

export async function logIn(login: string, password: string): Promise<LoginResp> {
    const bodyData = { login: login, password: password };
    
    return axios
        .post(API.LOGIN, bodyData, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as LoginResp;
        }).catch((reason) => {
            return {success: false, dfs_token_active: false, token: "", role: Role.User, user_id: 0, errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as LoginResp;
        });
}

export async function logOut(): Promise<SuccessResponse> {

    return axios
        .post(API.LOGOUT, {}, {timeout: simpleOperationsTimeout})
        .then(({data}) => {
            return data as SuccessResponse;
        }).catch((reason) => {
            return {success: false, errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as SuccessResponse;
        });
}
// endregion
// region Admin Users Operations
export async function getUsers(): Promise<GetUsersResp> {
    return axios
        .get(API.ADMIN_USERS, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as GetUsersResp;
        }).catch((reason) => {
            return {users: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as GetUsersResp;
        });
}

export async function createUser(login: string, password: string): Promise<UpdateUserResp> {
    const bodyData = { login: login, password: password };
    return axios
        .post(API.ADMIN_USERS, bodyData, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as UpdateUserResp;
        }).catch((reason) => {
            return {success: false, message: "", users: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateUserResp;
        });
}

export async function promoteUser(user_id: number): Promise<UpdateUserResp> {
    return axios
        .post(API.ADMIN_USERS + '/'+user_id, {}, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as UpdateUserResp;
        }).catch((reason) => {
            return {success: false, message: "", errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateUserResp;
        });
}

export async function demoteUser(user_id: number): Promise<UpdateUserResp> {
    return axios
        .put(API.ADMIN_USERS + '/'+user_id, {}, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as UpdateUserResp;
        }).catch((reason) => {
            return {success: false, message: "", errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateUserResp;
        });
}

export async function deleteUser(user_id: number): Promise<UpdateUserResp> {
    return axios
        .delete(API.ADMIN_USERS + '/'+user_id, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateUserResp;
        }).catch((reason) => {
            return {success: false, message: "", errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateUserResp;
        });
}
// endregion
// region Admin Keywords Operations
export async function getDivisionsFull(): Promise<GetDivisionsFullResp> {
    return axios
        .get(API.ADMIN_KW_DIV_FULL, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as GetDivisionsFullResp;
        }).catch((reason) => {
            return {divisions: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as GetDivisionsFullResp;
        });
}

export async function createDivision(division_name: string): Promise<UpdateDivisionResp> {
    const bodyData = { division_name: division_name };
    return axios
        .post(API.ADMIN_KW_DIV, bodyData, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as UpdateDivisionResp;
        }).catch((reason) => {
            return {success: false, message: "", divisions: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateDivisionResp;
        });
}

export async function renameDivision(div_id: number, division_name: string): Promise<UpdateDivisionResp> {
    const bodyData = { division_name: division_name };

    return axios
        .put(API.ADMIN_KW_DIV+'/'+div_id, bodyData, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateDivisionResp;
        }).catch((reason) => {
            return {success: false, message: "", divisions: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateDivisionResp;
        });
}

export async function deleteDivision(div_id: number): Promise<UpdateDivisionResp> {
    return axios
        .delete(API.ADMIN_KW_DIV+'/'+div_id, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateDivisionResp;
        }).catch((reason) => {
            return {success: false, message: "", divisions: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateDivisionResp;
        });
}


export async function getDivisionAttributes(division_id: number): Promise<GetDivisionAttributesResp> {
    return axios
        .get(API.ADMIN_KW_DIV+"/"+division_id+"/attributes", {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as GetDivisionAttributesResp;
        }).catch((reason) => {
            return {division_attributes: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as GetDivisionAttributesResp;
        });
}

export async function createDivisionAttribute(division_id: number, attribute_name: string): Promise<UpdateDivisionAttributeResp> {
    const bodyData = { attribute_name: attribute_name };
    return axios
        .post(API.ADMIN_KW_DIV+"/"+division_id+"/attributes", bodyData, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as UpdateDivisionAttributeResp;
        }).catch((reason) => {
            return {success: false, message: "", division_attributes: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateDivisionAttributeResp;
        });
}

export async function deleteDivisionAttribute(division_id: number, att_id: number): Promise<UpdateDivisionAttributeResp> {
    return axios
        .delete(API.ADMIN_KW_DIV+"/"+division_id+"/attributes/"+att_id, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateDivisionAttributeResp;
        }).catch((reason) => {
            return {success: false, message: "", divisions: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateDivisionAttributeResp;
        });
}

export async function getCategories(division_id: number): Promise<GetCategoriesResp> {
    return axios
        .get(API.ADMIN_KW_DIV+"/"+division_id+"/categories", {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as GetCategoriesResp;
        }).catch((reason) => {
            return {categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as GetCategoriesResp;
        });
}

export async function createCategory(division_id: number, CSI_number: string, CSI_name: string): Promise<UpdateCategoryResp> {
    const bodyData = { CSI_number: CSI_number, CSI_name: CSI_name };
    return axios
        .post(API.ADMIN_KW_DIV+"/"+division_id+"/categories", bodyData, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function renameCategory(division_id: number, cat_id: number, CSI_number: string, CSI_name: string): Promise<UpdateCategoryResp> {
    const bodyData = { CSI_number: CSI_number, CSI_name: CSI_name };

    return axios
        .put(API.ADMIN_KW_DIV+"/"+division_id+"/categories/"+cat_id, bodyData, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function deleteCategory(division_id: number, cat_id: number): Promise<UpdateCategoryResp> {
    return axios
        .delete(API.ADMIN_KW_DIV+"/"+division_id+"/categories/"+cat_id, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function createIndicator(division_id: number, cat_id: number, compared_value: string, operator: AttTestOperator, compared_column_ids: number[], sibling_id: number = 0, sibling_type: IndicatorSiblingType = IndicatorSiblingType.NoSibling, create_group: boolean = true, group_operation: GroupOperation = GroupOperation.And): Promise<UpdateCategoryResp> {
    const bodyData = {
        compared_value: compared_value,
        operator: operator.valueOf(),
        compared_column_ids: compared_column_ids,
        sibling_id: sibling_id,
        sibling_type: sibling_type.valueOf(),
        create_group: create_group,
        group_operation: group_operation.valueOf()
    };

    return axios
        .post(API.ADMIN_KW_DIV+"/"+division_id+"/categories/"+cat_id, bodyData, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function updateIndicator(ind_id: number, compared_value: string, operator: AttTestOperator, compared_column_ids: number[]): Promise<UpdateCategoryResp> {
    const bodyData = {
        compared_value: compared_value,
        operator: operator.valueOf(),
        compared_column_ids: compared_column_ids,
    };

    return axios
        .put(API.ADMIN_KW_IND+ind_id, bodyData, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function deleteIndicator(ind_id: number): Promise<UpdateCategoryResp> {
    return axios
        .delete(API.ADMIN_KW_IND+ind_id, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function flattenGroup(group_id: number): Promise<UpdateCategoryResp> {
    return axios
        .post(API.ADMIN_KW_GR+group_id, {}, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function updateGroup(group_id: number, bool_operation: GroupOperation): Promise<UpdateCategoryResp> {
    const bodyData = {
        bool_operation: bool_operation.valueOf()
    };

    return axios
        .put(API.ADMIN_KW_GR+group_id, bodyData, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function deleteGroup(group_id: number): Promise<UpdateCategoryResp> {
    return axios
        .delete(API.ADMIN_KW_GR+group_id, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as UpdateCategoryResp;
        }).catch((reason) => {
            return {success: false, message: "", categories: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as UpdateCategoryResp;
        });
}

export async function exportKeywords(readable: boolean): Promise<boolean> {
    try {
        const response = await axios
            .post((readable? API.ADMIN_KW_EXPORT_R : API.ADMIN_KW_EXPORT), {}, {responseType: "blob", timeout: mediumOperationsTimeout});

        const contentDisposition = response.headers["content-disposition"];
        let filename = `exported-keywords.${readable? "txt" : "json"}`;

        if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match && match[1]) {
                filename = match[1];
            }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return true;
    } catch (error) {
        console.error("Error downloading file:", error);
        return false;
    }
}

export async function uploadCompressedKeywords(file: File): Promise<GetDivisionsFullResp> {
    const buffer = await file.arrayBuffer();
    const gzipped = pako.gzip(new Uint8Array(buffer));
    const compressedBlob = new Blob([gzipped], { type: 'application/gzip' });

    const formData = new FormData();
    formData.append("file", compressedBlob, file.name);

    return axios
        .post(API.ADMIN_KW_IMPORT, formData, {
            headers: { "Content-Encoding": "gzip" },
            timeout: complexOperationsTimeout
        })
        .then(({ data }) => data as GetDivisionsFullResp)
        .catch((reason) => ({divisions: [], errorCode: reason.response?.status ?? reason.request?.status ?? 500
        }));
}
// endregion
// region CSI Assign
export async function getDivisions(): Promise<GetDivisionsResp> {
    return axios
        .get(API.ADMIN_KW_DIV, {timeout: mediumOperationsTimeout})
        .then(({data}) => {
            return data as GetDivisionsResp;
        }).catch((reason) => {
            return {divisions: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as GetDivisionsResp;
        });
}

export async function uploadCompressedCsv(ai: boolean, file: File, division_id: number = 0): Promise<UploadCSVResp> {
    const buffer = await file.arrayBuffer();
    const gzipped = pako.gzip(new Uint8Array(buffer));
    const compressedBlob = new Blob([gzipped], { type: 'application/gzip' });

    const formData = new FormData();
    formData.append("file", compressedBlob, file.name);

    return axios
        .post((ai? API.CSI_UPLOAD_AI : API.CSI_UPLOAD_KW+division_id), formData, {
            headers: { "Content-Encoding": "gzip" },
            timeout: complexOperationsTimeout
        })
        .then(({ data }) => data as UploadCSVResp)
        .catch((reason) => ({success: false, rowCount: 0, unique_id: "", message: "An error occurred!", errorCode: reason.response?.status ?? reason.request?.status ?? 500
        }));
}

export async function checkAssignStatus(unique_id: string): Promise<SuccessResponse> {
    return axios
        .get(API.CSI_STATUS+unique_id, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as SuccessResponse;
        }).catch((reason) => {
            return {success: false, errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as SuccessResponse;
        });
}

export async function exportResults(unique_id: string): Promise<boolean> {
    try {
        const response = await axios.get(API.CSI_DOWNLOAD+unique_id, {responseType: "blob", timeout: mediumOperationsTimeout});

        const contentDisposition = response.headers["content-disposition"];
        let filename = `${unique_id}.xlsx`;

        if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match && match[1]) {
                filename = match[1];
            }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return true;
    } catch (error) {
        console.error("Error downloading file:", error);
        return false;
    }
}

export async function getHistory(ai: boolean): Promise<HistoryResp> {
    return axios
        .get(API.CSI_HISTORY+(ai? "ai" : "kw"), {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as HistoryResp;
        }).catch((reason) => {
            return {history: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as HistoryResp;
        });
}

export async function deleteHistoryFile(unique_id: string): Promise<HistoryResp> {
    return axios
        .delete(API.CSI_HISTORY+unique_id, {timeout: complexOperationsTimeout})
        .then(({data}) => {
            return data as HistoryResp;
        }).catch((reason) => {
            return {history: [], errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as HistoryResp;
        });
}
// endregion
// export async function clearAllCache(): Promise<SuccessResponse> {
//     return axios
//         .delete(API.CLEAR_CACHE, {timeout: complexOperationsTimeout})
//         .then(({data}) => {
//             return data as SuccessResponse;
//         }).catch((reason) => {
//             return {success: false, errorCode: reason.response !== undefined? reason.response.status : reason.request.status } as SuccessResponse;
//         });
// }